import { useNavigation } from "@react-navigation/native";
import { ChevronLeft, ChevronRight, Pen, Trash2 } from "@tamagui/lucide-icons";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import { sanitizeForFirestore } from "../../../modules/core/sanitizeForFirestore";
import { Permission } from "../../../permissions/types";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { IconPill, PillType } from "../../../ui/Pill";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { Title } from "../../../ui/Title";
import { EditorRender } from "../../../ui/YooptaEditor";
import { BusinessScheduleItemForm } from "../../schedule/BusinessScheduleItemForm";
import {
  type Event,
  type EventModule,
  isEvent,
  isEventModule,
  isEventTeamModule,
} from "../../scheduleTypes";
import type { BusinessPlanModel } from "../BusinessPlanModel";
import { useScheduleItems } from "./useScheduleItems";

type BusinessPlanScheduleProps = {
  businessPlanUid: string;
  itemUid: string;
};

const EditEventForm: React.FC<{
  businessPlan: BusinessPlanModel;
  item: Event;
}> = observer(({ businessPlan, item }) => {
  const { businessScheduleItemApi } = useApi();
  const { businessScheduleStore } = useStores();
  const [showForm, setShowForm] = useState(false);
  const { navigate } = useNavigation();

  if (!showForm) {
    return (
      <>
        {businessPlan.checkPermission(Permission.CanEditSchedules) && (
          <XStack gap="$small">
            <IconPill
              text="Edit event"
              icon={Pen}
              type={PillType.Primary}
              onPress={() => setShowForm(true)}
            />
            <PopoverConfirm
              confirmTitle={"Are you sure you want to remove this event?"}
              onConfirm={() => {
                businessScheduleItemApi.delete(item);
                navigate("BusinessSchedule", {
                  businessUid: businessPlan.uid,
                });
              }}
            >
              <IconPill
                textColor="#fff"
                text="Delete"
                icon={Trash2}
                type={PillType.Danger}
              />
            </PopoverConfirm>
          </XStack>
        )}
      </>
    );
  }

  const scheduleItem = businessScheduleStore.byId.get(item.uid);

  if (!scheduleItem) {
    return null;
  }

  return (
    <BusinessScheduleItemForm
      data={scheduleItem.data}
      businessPlanStartingDate={businessPlan.data.eventInfo.date}
      onSave={(data) => {
        if (!(data.date || data.offsetInDays) || !data.title) {
          return;
        }

        businessScheduleItemApi.update(item.uid, {
          businessPlanUid: businessPlan.uid,
          date: data.date ?? null,
          offsetInDays: data.offsetInDays ?? null,
          emoji: data.emoji ?? null,
          title: data.title,
          description: sanitizeForFirestore(data.description ?? null),
          schedule: data.schedule ?? null,
        });

        setShowForm(false);
      }}
    />
  );
});

const BusinessPlanScheduleItemUIEvent: React.FC<{ item: Event }> = observer(
  ({ item }) => {
    return (
      <>
        <YStack gap="$medium">
          {item.description && <EditorRender value={item.description} />}
        </YStack>
        {item.schedule && (
          <Group>
            {item.schedule.map((scheduleItem) => {
              return (
                <XStack key={scheduleItem.uid} width="100%">
                  <Label width={80}>
                    {scheduleItem.startTime}
                    {scheduleItem.startTime && scheduleItem.endTime ? (
                      <br />
                    ) : (
                      ""
                    )}
                    {scheduleItem.endTime}
                  </Label>
                  <Body>{scheduleItem.title}</Body>
                </XStack>
              );
            })}
          </Group>
        )}
      </>
    );
  },
);

const BusinessPlanScheduleItemUIModule: React.FC<{ item: EventModule }> =
  observer(({ item }) => {
    return <>{item.module.render()}</>;
  });

export const BusinessPlanScheduleItemUI = observer(
  ({ businessPlanUid, itemUid }: BusinessPlanScheduleProps) => {
    const { businessPlansStore } = useStores();
    const { navigate } = useNavigation();

    const businessPlan = businessPlansStore.byId.get(businessPlanUid);

    const { groupedList } = useScheduleItems(businessPlanUid, true);

    const { previousItem, nextItem, currentItem } = useMemo(() => {
      const realItems = groupedList.filter(
        (listItem): listItem is EventModule | Event =>
          isEventModule(listItem) ||
          isEvent(listItem) ||
          isEventTeamModule(listItem),
      );
      const currentIndex = realItems.findIndex(
        (listItem) => listItem.uid === itemUid,
      );

      let previousItem: EventModule | Event | undefined;
      let nextItem: EventModule | Event | undefined;
      if (currentIndex !== -1) {
        previousItem = realItems[currentIndex - 1];
        nextItem = realItems[currentIndex + 1];
      }

      return { previousItem, nextItem, currentItem: realItems[currentIndex] };
    }, [groupedList, itemUid]);

    if (!businessPlan || !currentItem) {
      return null;
    }

    return (
      <View>
        <YStack gap="$medium">
          {isEvent(currentItem) ? (
            <EditEventForm item={currentItem} businessPlan={businessPlan} />
          ) : (
            <View height={28} />
          )}
          <Box paddingBottom={0}>
            <YStack gap="$larger">
              <XStack width="100%" justifyContent="space-between">
                <View
                  flexShrink={0}
                  width={80}
                  disabled={!previousItem}
                  opacity={previousItem ? 1 : 0.5}
                  cursor="pointer"
                  onPress={() =>
                    previousItem &&
                    navigate("BusinessScheduleItem", {
                      businessUid: businessPlanUid,
                      itemUid: previousItem?.uid,
                    })
                  }
                >
                  <ChevronLeft />
                </View>
                <YStack
                  justifyContent="center"
                  alignItems="center"
                  gap="$small"
                  flex={1}
                >
                  <Label>
                    {dayjs(currentItem.date).format("ddd MMMM YYYY")}
                  </Label>
                </YStack>
                <View
                  flexShrink={0}
                  alignItems="flex-end"
                  width={80}
                  disabled={!nextItem}
                  opacity={nextItem ? 1 : 0.5}
                  cursor="pointer"
                  onPress={() =>
                    nextItem &&
                    navigate("BusinessScheduleItem", {
                      businessUid: businessPlanUid,
                      itemUid: nextItem?.uid,
                    })
                  }
                >
                  <ChevronRight />
                </View>
              </XStack>

              <Title>{currentItem.title}</Title>
            </YStack>
          </Box>
        </YStack>

        {isEvent(currentItem) && (
          <Box>
            <BusinessPlanScheduleItemUIEvent item={currentItem} />
          </Box>
        )}
        {(isEventModule(currentItem) || isEventTeamModule(currentItem)) && (
          <BusinessPlanScheduleItemUIModule item={currentItem} />
        )}
      </View>
    );
  },
);
